import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Eye, EyeOff, LogIn, Zap } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { loginSchema } from '../../validations/auth.validation';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import useTranslation from '../../hooks/useTranslation';

const DEMO_CREDENTIALS = {
  email: 'operator@studyspace.com',
  password: 'Operator@123'
};

const LoginPage = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || null;

  const initialValues = { email: '', password: '' };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const result = await login(values);
      if (result.success) {
        if (from) navigate(from, { replace: true });
      } else {
        if (result.error?.includes('email')) setFieldError('email', result.error);
        else if (result.error?.includes('password')) setFieldError('password', result.error);
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold text-white">W</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.welcomeBack')}</h1>
          <p className="mt-2 text-gray-600">{t('auth.signInToAccount')}</p>
        </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
          <Formik
            initialValues={credentials}
            enableReinitialize
            validationSchema={loginSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched, setFieldValue }) => (
              <Form className="space-y-6">
                <div>
                  <label htmlFor="email" className="label">{t('auth.email')}</label>
                  <Field
                    id="email" name="email" type="email" autoComplete="email"
                    placeholder={t('auth.email')}
                    className={`input-base ${errors.email && touched.email ? 'input-error' : ''}`}
                  />
                  <ErrorMessage name="email" component="p" className="error-message" />
                </div>

                <div>
                  <label htmlFor="password" className="label">{t('auth.password')}</label>
                  <div className="relative">
                    <Field
                      id="password" name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={t('auth.password')}
                      className={`input-base pe-12 ${errors.password && touched.password ? 'input-error' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <ErrorMessage name="password" component="p" className="error-message" />
                </div>

                <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2 py-3">
                  {isSubmitting ? (
                    <><LoadingSpinner size="sm" className="text-white" /><span>{t('auth.signingIn')}</span></>
                  ) : (
                    <><LogIn className="h-5 w-5" /><span>{t('auth.signIn')}</span></>
                  )}
                </button>
              </Form>
            )}
          </Formik>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">{t('auth.quickLogin')}</p>
            <button
              type="button"
              onClick={() => {
                setCredentials(DEMO_CREDENTIALS);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 rounded-xl border border-green-200 hover:bg-green-100 transition-colors text-sm font-medium"
            >
              <Zap className="h-4 w-4" />
              <span>{t('auth.loginAsAdmin')}</span>
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('auth.dontHaveAccount')}{' '}
              <Link to="/signup" className="font-medium text-primary-600 hover:text-primary-700">
                {t('auth.signUp')}
              </Link>
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} {t('app.name')}. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
